import { useRef, useCallback } from "react";
import { streamApi, RateLimitError } from "../api.js";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
import { extractStreamingProductGroups, processProductGroupTags } from "../utils/productGroupParser.js";
const useStreamingSearch = ({
  brandId,
  appConfigId,
  conversationState,
  scrollToBottom,
  shouldScrollOnNextMessageRef,
  onStreamError,
  devOptions
}) => {
  const {
    conversationId,
    setConversationId,
    setIsActivelyStreaming,
    setStreamingAIResponse,
    setCurrentStatus,
    setConversationHistory,
    setIsWaitingForFollowUpQueries,
    setStreamingProductGroups,
    setProductMetadataMap,
    setSurveyMetadataMap,
    setPreviewSearchQueries,
    setPreviewQueryProducts,
    setFollowUpQuestions,
    setStreamingFeaturedProduct,
    setStreamingStoreLocator,
    setStreamingLoginRequired,
    setStreamingDevInfo,
    clearStreamingState,
    apiBaseUrl,
    isSharedView,
    sharedAtMessageIndex,
    setIsSharedView,
    setSourceConversationId,
    setSourceCheckpointIndex
  } = conversationState;
  const abortControllerRef = useRef(null);
  const cancelStream = useCallback(() => {
    var _a;
    (_a = abortControllerRef.current) == null ? void 0 : _a.abort();
  }, []);
  const performSearch = useCallback(
    async (searchTerm, imageData, imageMimeType, skipAddingUserMessage) => {
      var _a;
      if (!searchTerm.trim() && !imageData) {
        setStreamingAIResponse("");
        return;
      }
      (_a = abortControllerRef.current) == null ? void 0 : _a.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      setIsActivelyStreaming(true);
      shouldScrollOnNextMessageRef.current = true;
      if (!skipAddingUserMessage) {
        const featuredProductRegex = /<product\s+featured="true"\s+stacklineSku="([^"]+)"(?:\s+question="([^"]+)")?\s*\/>/;
        const featuredMatch = searchTerm.match(featuredProductRegex);
        const displayContent = featuredMatch && featuredMatch[2] ? featuredMatch[2] : searchTerm;
        const userMessage = {
          role: "user",
          content: displayContent,
          timestamp: /* @__PURE__ */ new Date(),
          // Include imageData for immediate display, imageUrl will come from backend on reload
          ...imageData && {
            imageData,
            imageMimeType
          }
        };
        setConversationHistory((prev) => [...prev, userMessage]);
      }
      setStreamingAIResponse("");
      setIsWaitingForFollowUpQueries(false);
      setStreamingProductGroups(/* @__PURE__ */ new Map());
      setPreviewSearchQueries([]);
      setPreviewQueryProducts([]);
      let fullBuffer = "";
      let currentProducts = [];
      let currentFollowUpQuestions = [];
      let messageAddedToHistory = false;
      let featuredProductData = null;
      let storeLocatorData = null;
      let capturedOrders = [];
      let capturedLoginRequired = null;
      let capturedSearchQueries = [];
      let capturedQueryProducts = [];
      let capturedDevInfo;
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        await streamApi({
          prompt: searchTerm,
          brandId: brandId || void 0,
          appConfigId: appConfigId || void 0,
          base64Image: imageData,
          imageMimeType,
          // imageUrl is now optional - backend will upload if not provided
          conversationId: conversationId || void 0,
          headersToInject: headers,
          apiBaseUrl,
          devOptions,
          signal: abortController.signal,
          onStatusChange: (status) => {
            setCurrentStatus(status);
          },
          onConversationId: (id) => {
            if (isSharedView && id !== conversationId) {
              setSourceConversationId(conversationId || void 0);
              setSourceCheckpointIndex(sharedAtMessageIndex);
              setIsSharedView(false);
            }
            setConversationId(id);
          },
          onNewProductMetadata: (products) => {
            setProductMetadataMap((prev) => {
              const newMap = new Map(prev);
              products.forEach((product) => {
                newMap.set(product.stacklineSku, product);
              });
              return newMap;
            });
            currentProducts = [...currentProducts, ...products];
            setStreamingAIResponse(processProductGroupTags(fullBuffer));
          },
          onFeaturedProduct: (product, question) => {
            featuredProductData = { product, question };
            setProductMetadataMap((prev) => {
              const newMap = new Map(prev);
              newMap.set(product.stacklineSku, product);
              return newMap;
            });
            setStreamingFeaturedProduct({ product, question });
            scrollToBottom();
          },
          onStoreLocator: (stacklineSku) => {
            storeLocatorData = { stacklineSku };
            setStreamingStoreLocator({ stacklineSku });
            scrollToBottom();
          },
          onOrders: (orders) => {
            capturedOrders = orders;
          },
          onSurveys: (surveys) => {
            setSurveyMetadataMap((prev) => {
              const newMap = new Map(prev);
              surveys.forEach((survey) => {
                newMap.set(survey.campaignId, survey);
              });
              return newMap;
            });
          },
          onEvent: (event) => {
            var _a2, _b;
            if (event.name === "search_products_queries") {
              const queries = Array.isArray(event.data) ? event.data : ((_a2 = event.data) == null ? void 0 : _a2.searchQueries) || [];
              capturedSearchQueries = queries;
              setPreviewSearchQueries(queries);
              if ((_b = event.data) == null ? void 0 : _b.devInfo) {
                capturedDevInfo = event.data.devInfo;
                setStreamingDevInfo(capturedDevInfo);
                console.log("[Streaming] Captured devInfo:", JSON.stringify(capturedDevInfo, null, 2));
              }
            } else if (event.name === "search_no_relevant_products") {
              console.log("[Streaming] No relevant products found for query", event.data);
              setPreviewQueryProducts([]);
            } else if (event.name === "search_query_products") {
              capturedQueryProducts = [...capturedQueryProducts, event.data];
              setPreviewQueryProducts(capturedQueryProducts);
            } else if (event.name === "follow_up_queries") {
              currentFollowUpQuestions = event.data;
              setIsWaitingForFollowUpQueries(false);
              setFollowUpQuestions(event.data);
              if (messageAddedToHistory) {
                setConversationHistory((prev) => {
                  const updated = [...prev];
                  const lastMessage = updated[updated.length - 1];
                  if (lastMessage && lastMessage.role === "assistant") {
                    updated[updated.length - 1] = {
                      ...lastMessage,
                      followUpQueries: event.data
                    };
                  }
                  return updated;
                });
              }
            } else if (event.name === "login_required") {
              conversationState.setStreamingLoginRequired(event.data);
              capturedLoginRequired = event.data;
            } else if (event.name === "message_output_created") {
              setIsActivelyStreaming(false);
              clearStreamingState();
              const assistantMessage = {
                role: "assistant",
                content: fullBuffer,
                timestamp: /* @__PURE__ */ new Date(),
                products: currentProducts,
                ...featuredProductData && {
                  featuredProduct: featuredProductData
                },
                ...storeLocatorData && {
                  storeLocator: storeLocatorData
                },
                ...capturedOrders.length > 0 && {
                  orders: capturedOrders
                },
                ...capturedLoginRequired && {
                  loginRequired: capturedLoginRequired
                },
                ...capturedSearchQueries.length > 0 && {
                  previewSearchQueries: capturedSearchQueries
                },
                ...capturedQueryProducts.length > 0 && {
                  previewQueryProducts: capturedQueryProducts
                },
                ...capturedDevInfo && {
                  devInfo: capturedDevInfo
                }
                // No follow-ups yet - they'll be added when they arrive
              };
              setConversationHistory((prev) => [...prev, assistantMessage]);
              messageAddedToHistory = true;
            }
          },
          onChunk: (chunk) => {
            try {
              const data = JSON.parse(chunk);
              if (data.type === "follow_up_questions" && data.content) {
                currentFollowUpQuestions = data.content;
                return;
              } else if (Array.isArray(data)) {
                return;
              }
            } catch {
            }
            if (chunk.includes("Stream completed.")) {
              return;
            }
            fullBuffer += chunk;
            const newStreamingGroups = extractStreamingProductGroups(fullBuffer);
            if (newStreamingGroups.size > 0) {
              setStreamingProductGroups(newStreamingGroups);
            }
            const processedText = processProductGroupTags(fullBuffer);
            setStreamingAIResponse(processedText);
            scrollToBottom();
          },
          onComplete: () => {
            setIsActivelyStreaming(false);
            clearStreamingState();
            if (!messageAddedToHistory) {
              const assistantMessage = {
                role: "assistant",
                content: fullBuffer,
                timestamp: /* @__PURE__ */ new Date(),
                products: currentProducts,
                ...featuredProductData && {
                  featuredProduct: featuredProductData
                },
                ...storeLocatorData && {
                  storeLocator: storeLocatorData
                },
                ...capturedOrders.length > 0 && {
                  orders: capturedOrders
                },
                ...capturedLoginRequired && {
                  loginRequired: capturedLoginRequired
                },
                ...capturedSearchQueries.length > 0 && {
                  previewSearchQueries: capturedSearchQueries
                },
                ...capturedQueryProducts.length > 0 && {
                  previewQueryProducts: capturedQueryProducts
                },
                ...capturedDevInfo && {
                  devInfo: capturedDevInfo
                },
                ...currentFollowUpQuestions.length > 0 && {
                  followUpQueries: currentFollowUpQuestions
                }
              };
              setConversationHistory((prev) => [...prev, assistantMessage]);
            } else if (currentFollowUpQuestions.length > 0) {
              setConversationHistory((prev) => {
                var _a2;
                const updated = [...prev];
                const lastMessage = updated[updated.length - 1];
                if (lastMessage && lastMessage.role === "assistant" && !((_a2 = lastMessage.followUpQueries) == null ? void 0 : _a2.length)) {
                  updated[updated.length - 1] = {
                    ...lastMessage,
                    followUpQueries: currentFollowUpQuestions
                  };
                }
                return updated;
              });
            }
            setFollowUpQuestions(currentFollowUpQuestions);
            if (currentFollowUpQuestions.length === 0) {
              setIsWaitingForFollowUpQueries(true);
              setTimeout(() => {
                setIsWaitingForFollowUpQueries(false);
              }, 3e3);
            }
          },
          onError: (error) => {
            var _a2, _b, _c, _d;
            console.error("Stream failed:", error);
            setIsActivelyStreaming(false);
            setCurrentStatus("");
            if (!skipAddingUserMessage) {
              setConversationHistory((prev) => prev.slice(0, -1));
            }
            let errorType = "unknown";
            if (error instanceof RateLimitError) {
              errorType = "rate_limit";
            } else if (error instanceof TypeError || ((_a2 = error.message) == null ? void 0 : _a2.includes("network")) || ((_b = error.message) == null ? void 0 : _b.includes("fetch"))) {
              errorType = "network";
            } else if (((_c = error.message) == null ? void 0 : _c.includes("5")) && ((_d = error.message) == null ? void 0 : _d.includes("status"))) {
              errorType = "server";
            }
            onStreamError == null ? void 0 : onStreamError(errorType, searchTerm);
          }
        });
      } catch (error) {
        console.error("Search failed:", error);
        setIsActivelyStreaming(false);
      }
    },
    [
      brandId,
      appConfigId,
      conversationId,
      scrollToBottom,
      setConversationHistory,
      setConversationId,
      setCurrentStatus,
      setFollowUpQuestions,
      setIsActivelyStreaming,
      setIsWaitingForFollowUpQueries,
      setPreviewQueryProducts,
      setPreviewSearchQueries,
      setProductMetadataMap,
      setSurveyMetadataMap,
      setStreamingAIResponse,
      setStreamingProductGroups,
      setStreamingFeaturedProduct,
      setStreamingStoreLocator,
      setStreamingLoginRequired,
      setStreamingDevInfo,
      clearStreamingState,
      shouldScrollOnNextMessageRef,
      apiBaseUrl,
      onStreamError,
      devOptions,
      isSharedView,
      sharedAtMessageIndex,
      setIsSharedView,
      setSourceConversationId,
      setSourceCheckpointIndex
    ]
  );
  return { performSearch, cancelStream };
};
export {
  useStreamingSearch
};
//# sourceMappingURL=useStreamingSearch.js.map
